import { useState, useEffect, useCallback, ReactNode } from 'react';
import { Canvas, Props as CanvasProps } from '@react-three/fiber';
import * as THREE from 'three';

// Check for WebGPU support
async function checkWebGPUSupport(): Promise<boolean> {
  if (!navigator.gpu) {
    return false;
  }

  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

interface WebGPUCanvasProps extends Omit<CanvasProps, 'gl'> {
  children: ReactNode;
  fallbackMessage?: string;
  onRendererReady?: (type: 'webgpu' | 'webgl') => void;
}

export function WebGPUCanvas({
  children,
  fallbackMessage = 'WebGPU not supported. Using WebGL fallback.',
  onRendererReady,
  ...props
}: WebGPUCanvasProps) {
  const [rendererType, setRendererType] = useState<'checking' | 'webgpu' | 'webgl' | 'error'>('checking');

  useEffect(() => {
    let mounted = true;

    async function detectRenderer() {
      const webgpuSupported = await checkWebGPUSupport();

      if (!mounted) return;

      if (webgpuSupported) {
        console.log('WebGPU supported - using WebGPU renderer');
        setRendererType('webgpu');
      } else {
        console.log('WebGPU not supported - falling back to WebGL');
        setRendererType('webgl');
      }
    }

    detectRenderer();

    return () => {
      mounted = false;
    };
  }, []);

  // WebGL renderer factory (fallback)
  const createWebGLRenderer = useCallback((canvas: HTMLCanvasElement) => {
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      stencil: true,
      powerPreference: 'high-performance',
    });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    
    onRendererReady?.('webgl');
    return renderer;
  }, [onRendererReady]);

  // WebGPU renderer factory
  const createWebGPURenderer = useCallback(async (canvas: HTMLCanvasElement) => {
    try {
      // Dynamic import for WebGPU renderer
      const { WebGPURenderer } = await import('three/webgpu');
      
      const renderer = new WebGPURenderer({
        canvas,
        antialias: true,
        alpha: false,
        stencil: true,
        powerPreference: 'high-performance',
      } as any);

      await renderer.init();
      
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.0;

      onRendererReady?.('webgpu');
      return renderer;
    } catch (error) {
      console.error('Failed to create WebGPU renderer:', error);
      // Fall back to WebGL
      setRendererType('webgl');
      return createWebGLRenderer(canvas);
    }
  }, [createWebGLRenderer, onRendererReady]);

  if (rendererType === 'checking') {
    return (
      <div className="webgpu-loading">
        <div className="loading-spinner" />
        <p>Initializing renderer...</p>
      </div>
    );
  }

  if (rendererType === 'error') {
    return (
      <div className="webgpu-error">
        <h2>Renderer Error</h2>
        <p>Failed to initialize graphics renderer.</p>
        <p>Please ensure your browser supports WebGL or WebGPU.</p>
      </div>
    );
  }

  return (
    <>
      {rendererType === 'webgl' && (
        <div className="webgl-fallback-notice">
          {fallbackMessage}
        </div>
      )}
      <Canvas
        gl={rendererType === 'webgpu' 
          ? (createWebGPURenderer as unknown as CanvasProps['gl']) 
          : (createWebGLRenderer as unknown as CanvasProps['gl'])}
        {...props}
      >
        {children}
      </Canvas>
    </>
  );
}

// Styles for loading/error states
const styles = `
  .webgpu-loading,
  .webgpu-error {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #0a0e14;
    color: #e6e6e6;
    font-family: 'Outfit', sans-serif;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: #00d4ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .webgpu-error h2 {
    color: #ff4757;
    margin-bottom: 8px;
  }

  .webgl-fallback-notice {
    position: fixed;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 193, 7, 0.9);
    color: #000;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 1000;
    font-family: 'Outfit', sans-serif;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);
}
